import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/useAuth';
import { getRoleView, isPathAllowedInView } from './components/roleViews';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingIndicator } from './components/LoadingIndicator';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { QueuesPage } from './pages/QueuesPage';
import { CasesPage } from './pages/CasesPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { EmployersPage } from './pages/EmployersPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { EmployerLookupPage } from './pages/EmployerLookupPage';
import { RevenuePage } from './pages/RevenuePage';
import { BillingPage } from './pages/BillingPage';
import { RetentionPage } from './pages/RetentionPage';
import { AdminConfigPage } from './pages/AdminConfigPage';
import { AdminPage } from './pages/AdminPage';
import { AiReviewPage } from './pages/AiReviewPage';
import { IntegrationPage } from './pages/IntegrationPage';
import { StateOpsPage } from './pages/StateOpsPage';
import { TeamPage } from './pages/TeamPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ReportsPage } from './pages/ReportsPage';
import { CfoPortalPage } from './pages/CfoPortalPage';
import { IntelligencePage } from './pages/IntelligencePage';
import { KnowledgePage } from './pages/KnowledgePage';
import { MemoryPage } from './pages/MemoryPage';
import { EvalsPage } from './pages/EvalsPage';
import { AgentFactoryPage } from './pages/AgentFactoryPage';
import { ConnectorsPage } from './pages/ConnectorsPage';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={center}>
        <LoadingIndicator text="Loading…" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/**
 * Role-first view guard. When the active view is a scoped persona, a route not
 * in that view's nav set (whether navigated to or deep-linked) is bounced to
 * the view's landing page so hidden surfaces aren't reachable while scoped. The
 * `all` view is unrestricted. This is presentation-layer scoping only — it
 * never alters the token or tenant, so API calls keep succeeding.
 */
function ViewGuard({ children }: { children: ReactNode }) {
  const { activeView } = useAuth();
  const location = useLocation();
  if (!isPathAllowedInView(activeView, location.pathname)) {
    return <Navigate to={getRoleView(activeView).landing} replace />;
  }
  return <>{children}</>;
}

function AuthenticatedLayout() {
  return (
    <ProtectedRoute>
      <AppShell>
        <ViewGuard>
        <Routes>
          <Route path="/queues" element={<QueuesPage />} />
          <Route path="/cases" element={<CasesPage />} />
          <Route path="/cases/:caseId" element={<CaseDetailPage />} />
          <Route path="/state-ops" element={<StateOpsPage />} />
          <Route path="/employers" element={<EmployersPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          {/* Appended pages (task-33) — distinct paths to avoid conflict with task-32. */}
          <Route path="/employer-lookup" element={<EmployerLookupPage />} />
          {/* Phase 3 (wireframe 3): Billing Work Queue + Retention workspace. */}
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/retention" element={<RetentionPage />} />
          {/* /revenue kept as a legacy alias so existing links stay valid. */}
          <Route path="/revenue" element={<RevenuePage />} />
          {/* Phase 4 (wireframe 4): supervisor Team, onboarding, admin, reports. */}
          <Route path="/team" element={<TeamPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          {/* Legacy alias for the standalone rules-config surface. */}
          <Route path="/admin/rules" element={<AdminConfigPage />} />
          <Route path="/ai-review" element={<AiReviewPage />} />
          {/* Phase 6 (wireframe 5, CFO + AI): CFO portal, intelligence, connectors. */}
          <Route path="/cfo-portal" element={<CfoPortalPage />} />
          <Route path="/intelligence" element={<IntelligencePage />} />
          <Route path="/intelligence/knowledge" element={<KnowledgePage />} />
          <Route path="/intelligence/memory" element={<MemoryPage />} />
          <Route path="/intelligence/evals" element={<EvalsPage />} />
          <Route path="/intelligence/agent-factory" element={<AgentFactoryPage />} />
          <Route path="/connectors" element={<ConnectorsPage />} />
          <Route path="/integration" element={<IntegrationPage />} />
          <Route path="*" element={<Navigate to="/queues" replace />} />
        </Routes>
        </ViewGuard>
      </AppShell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={<AuthenticatedLayout />} />
        </Routes>
      </AuthProvider>
    </ErrorBoundary>
  );
}

const center: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
};
